import { MagicLayout } from '@magic/layout';
import { PaylineController } from 'controllers/stateControllers/Payline.controller';
import type { ReelController } from 'controllers/stateControllers/Reel.controller';
import type {
    RoundData,
    RoundState,
    SymbolCell,
    WinLine,
    WinPosition,
} from '../../utils/api.types';
import { isFreeSpinsActive, setFreeSpinsActive, setGameState } from '../../stores/api.store';
import type { Paylines } from 'config/paylines.config';
import { wait } from '@magic/utils';
import { getNextStateData } from '../../utils/state.utils';
import { settingsStore } from '../../stores/settings.store';
import {
    clearFreeSpinsProgress,
    getFreeSpinsProgress,
    saveFreeSpinsProgress,
} from '../../utils/freeSpinsStorage';

// TODO: review thsi whole controllers system to be universal, simple and easily extendable for different game states and features (free spins, bonus games, etc). Maybe we can have some BaseStateController with common methods and then extend it for each state type?

/**
 * Win amounts arrive in minor units (6250 -> 62.50). Non-numeric values
 * (the `-` placeholder, undefined) pass through untouched.
 */
const formatWin = (win: number | string | undefined): string => {
    const amount = Number(win);

    return Number.isFinite(amount) ? (amount / 100).toFixed(2) : `${win ?? ''}`;
};

export class RoundController {
    private controllers: Map<string, unknown> = new Map();

    /**
     * Whether the free spins feature is running. Kept in the store rather than
     * on the controller so the API layer can pick the play mode (FREESPINS vs
     * BASE) without reaching into the round controller.
     */
    get freeSpinsActive(): boolean {
        return isFreeSpinsActive();
    }

    set freeSpinsActive(active: boolean) {
        setFreeSpinsActive(active);
    }

    /** betID of the round currently playing — tags persisted free spins progress. */
    private currentBetID = 0;
    /** last known free spins total — a retrigger only reports the new total,
     *  so the popup needs this to show how many spins were just added */
    private freeSpinsTotal = 0;

    /** The round being played has multiple reveals (free spins / bonus). */
    private multiRevealRound = false;

    /** Last balance shown, to pick the up/down animation. Undefined until the first one lands. */
    private balance?: number;

    constructor(
        private spine: MagicLayout,
        private reelController: ReelController,
        /** Confirms a played book event index to the backend (fire-and-forget). */
        private recordEvent: (index: number) => void,
    ) {
        this.controllers.set('payline', new PaylineController(this.spine));

        this.spine.texts.set('freeSpinsProgress', ``);
    }

    async playState(round: RoundData) {
        this.currentBetID = round.betID;
        const states = (round.state ?? []) as RoundState[];

        // multi-reveal books (free spins / bonus) report played reveals to the
        // backend so it can track progress through the round (see the reveal case)
        this.multiRevealRound = states.filter((state) => state.type === 'reveal').length > 1;

        // on a reload the backend replays the still-active round from the start;
        // if we have saved progress for it, skip ahead to the free spin the
        // player was on instead of replaying the feature from the trigger
        const startIndex = await this.resolveFreeSpinsResume(round.betID, states);

        for (let index = startIndex; index < states.length; index++) {
            await this.playQueueState(states[index], index);
        }
    }

    /**
     * Decides where to start playing a round. Returns 0 for a normal round, or
     * the index of the current free spin when resuming a reloaded round: it
     * restores the free spins UI and waits for the player to dismiss a "spins
     * left" popup before the round loop continues from that spin.
     */
    private async resolveFreeSpinsResume(betID: number, states: RoundState[]): Promise<number> {
        const progress = getFreeSpinsProgress();

        if (!progress) return 0;

        // progress tagged with another round is stale — drop it and start fresh
        if (progress.betID !== betID) {
            clearFreeSpinsProgress();
            return 0;
        }

        const resumeState = states[progress.resumeIndex];

        if (resumeState?.type !== 'updateFreeSpin') {
            clearFreeSpinsProgress();
            return 0;
        }

        await this.resumeFreeSpins(resumeState);

        return progress.resumeIndex;
    }

    /**
     * Restores the free spins presentation after a reload and holds until the
     * player acknowledges how many spins are left; the round loop then replays
     * the spin they were on and continues to the end of the feature.
     */
    private async resumeFreeSpins(resumeState: Extract<RoundState, { type: 'updateFreeSpin' }>) {
        this.freeSpinsActive = true;

        // re-enter free spins mode: the trigger normally hides the spin button
        // (state_freeSpinTrigger -> hide_spin_button), but it's skipped on
        // resume, so replay it here or the button stays visible until freeSpinEnd.
        // Awaited so it finishes before the popup: while it counts as active it
        // pushes show_popup to a higher track, and the close-click hide_popup
        // would later land on a lower track, masked by show_popup's end pose.
        await this.spine.animations.playState('freeSpinTrigger');

        // give the reels a board behind the popup instead of the empty default pose
        this.reelController.fillBoardWithRandomSymbols();

        // the spin the player is on, counting the one about to be replayed
        const progressText = `${resumeState.amount + 1} OF ${resumeState.total}`;
        this.spine.texts.set('freeSpinsProgress', progressText);

        await this.showPopup({
            title: `free spins left`,
            content: progressText,
            buttonText: `continue`,
        });
    }

    private async playQueueState(state: RoundState, index: number) {
        if (settingsStore.getState().roundDebug) {
            console.groupCollapsed(`🧟 state`, state?.type);
            console.table(state);
            console.groupEnd();
        }

        this.spine.animations.playState(state?.type);

        switch (state?.type) {
            case 'reveal': // 1 (after aech spin)
                // confirm progress through multi-reveal books to the backend
                // (mirrors the official web-sdk: it records every reveal of a
                // bonus book via /bet/event so the RGS can resume/settle it)
                if (this.multiRevealRound) {
                    this.recordEvent(state.index);
                }

                // free spin rounds arrive without a player-initiated spin —
                // start the reels (no-op when already spinning) so each free
                // spin plays a real spin instead of swapping the board in place
                if (state.gameType === 'freegame') {
                    this.spine.animations.playByName('blur', true);
                    this.reelController.spin();
                }

                // reelController.reveal applies the board instantly when the
                // reels are not spinning (initial reveal of a restored round)
                await this.revealResult(state.board, state.anticipation);

                if (state.gameType === 'freegame') {
                    this.freeSpinsActive = true;
                }
                // all symbols results are set
                break;

            case 'winInfo': // 2 (only if user wins something)
                this.onWinInfo(state);
                break;

            case 'setWin': // 4 (only if user wins something)
                if (state.winLevel > 0) {
                    switch (true) {
                        case state.winLevel > 5:
                            this.spine.animations.playState('bigWin');
                            break;
                        case state.winLevel > 7:
                            this.spine.animations.playState('megaWin');
                            break;
                        case state.winLevel > 9:
                            this.spine.animations.playState('epicWin');
                            break;
                    }
                }

                await this.setWinAmountText(state.amount);

                await this.spine.animations.stopState('setWin');

                break;

            case 'wincap': // 3 (only if user got big win)
                this.spine.animations.playState('bigWin');

                await this.showPopup({
                    title: `big win`,
                    content: formatWin(state.amount),
                    buttonText: `collect`,
                });
                break;

            case 'setTotalWin': // 5 (after each spin)
                await this.setTotalWin(state.amount);

                if (Number(state.amount) > 0 || this.freeSpinsActive) {
                    await wait(1000);
                }
                break;

            case 'finalWin':
                // this.setTotalWin(state.amount);
                setGameState('idle');

                // the round is still open here — Main.controller settles it once
                // this sequence returns, which is what credits the payout and
                // counts the balance up while the win is on screen

                if (Number(state.amount) > 0 || this.freeSpinsActive) {
                    await wait(1000);
                }
                break;

            // FreeSpins -----------------------------

            case 'freeSpinTrigger':
                this.freeSpinsTotal = state.totalFs;
                await this.showFreeSpinsTriggerSymbols(state.positions);

                wait(300).then(() => {
                    this.spine.texts.set('popup_content', String(state.totalFs), true);
                });

                await this.showPopup({
                    title: `free spins`,
                    content: `0`,
                    buttonText: `play`,
                });
                break;

            case 'freeSpinRetrigger': {
                // show only the spins this retrigger added, not the new total
                const addedFs = state.totalFs - this.freeSpinsTotal;
                this.freeSpinsTotal = state.totalFs;

                await this.showFreeSpinsTriggerSymbols(state.positions);

                wait(300).then(() => {
                    this.spine.texts.set('popup_content', String(addedFs), true);
                });

                await this.showPopup({
                    title: `More free spins!!!`,
                    buttonText: `play`,
                });
                break;
            }

            case 'updateFreeSpin': // on every free spin started
                this.freeSpinsTotal = state.total;
                this.spine.texts.set('freeSpinsProgress', `${state.amount + 1} OF ${state.total}`);
                // remember the spin we're on so a reload can resume from here
                saveFreeSpinsProgress({ betID: this.currentBetID, resumeIndex: index });
                break;

            case 'freeSpinEnd':
                // the round is NOT settled here: it stays open — with the saved
                // progress pointing at the last free spin — until the whole
                // sequence has played out, so a refresh on the feature win popup
                // resumes the round from that spin instead of losing it.
                // Main.controller settles it (and drops the progress) once this
                // popup has been dismissed and the book has finished.

                wait(300).then(() => {
                    this.spine.texts.set('popup_content', formatWin(state.amount), true);
                });

                // if (state.winLevel > 0) {
                // TODO: show big win animation if win level is big enough
                // }

                await this.showPopup({
                    title: `free spins win`,
                    content: `0`,
                    buttonText: `OK`,
                });

                this.freeSpinsActive = false;
                break;

            default:
                console.warn(`Unhandled state type: ${state.type}`);
        }
    }

    private async onWinInfo(state: Extract<RoundState, { type: 'winInfo' }>) {
        const nextSetWin = getNextStateData(state, 'setWin');

        // a big win takes over the plain win text (the following setWin state
        // plays its own presentation), but the paylines and the win symbol
        // animations always show
        if (!nextSetWin || !(nextSetWin.winLevel > 0)) {
            this.spine.texts.set('win', formatWin(state.totalWin), true);
            this.spine.animations.playState('winInfo');
        }

        this.showPaylines(state.wins);
        await this.activateWinSymbols(state.wins);

        await wait(1500);

        // restore the board after the win display, otherwise the dimmed
        // (inactive) symbols keep their swapped attachment into idle
        this.spine.animations.playByName('idle', true);
    }

    private async showPopup(text?: {
        title?: string;
        content?: string;
        buttonText?: string;
    }): Promise<void> {
        const { title, content, buttonText } = text || {};

        if (title) this.spine.texts.set('popup_title', title);
        if (content) {
            this.spine.texts.set('popup_content', '0');
            this.spine.texts.set('popup_content', content, true);
        }
        if (buttonText) this.spine.texts.set('button_popup_close', buttonText);

        await this.spine.animations.playState(`popup`);

        return new Promise((resolve) => {
            this.spine.animations.addEventListener(`popup_close_click`, async () => {
                // drop show_popup's lingering end pose: depending on what else
                // was animating when the popup opened, hide_popup can land on a
                // lower track and the stale pose above it would mask the close
                // animation (listeners run before playEvent starts hide_popup,
                // so the track is free by the time it plays)
                this.spine.animations.stopState('popup', false);
                resolve();
            });
        });
    }

    private async revealResult(board: SymbolCell[][], anticipation?: number[]) {
        // the reels fill with the result symbols and stop one by one;
        // when this resolves the visible board is final
        await this.reelController.reveal(board, anticipation);

        // solo, so the symbols drop their blur state entirely instead of
        // stacking idle on another track above it
        this.spine.animations.playByName('idle', true);
    }

    private async activateWinSymbols(wins: WinLine[]) {
        await this.activateSymbols(wins.flatMap((win) => win.positions));
    }

    private async showFreeSpinsTriggerSymbols(positions: WinPosition[]) {
        await this.activateSymbols(positions);
    }

    /**
     * Plays `active` on the symbols at the given board positions and
     * `inactive` on every other visible symbol. Positions outside the
     * visible window are dropped — hidden buffer symbols never animate.
     *
     * The symbol spines show exactly one state at a time (idle/blur/
     * active/inactive), so the animations play solo: stacking them on
     * extra tracks leaves stale entries that re-apply their attachment
     * over later states (e.g. inactive gravestones during the next spin).
     */
    private async activateSymbols(positions: WinPosition[]) {
        const activeIDs = new Set(
            positions.map(({ reel, row }) => this.getSymbolIDByPosition(reel, row)).filter(Boolean),
        );

        if (!activeIDs.size) return;

        await Promise.all(
            this.reelController
                .getVisibleSymbolIDs()
                .map((symbolID) =>
                    this.spine.animations.play(
                        symbolID,
                        activeIDs.has(symbolID) ? 'active' : 'inactive',
                        true,
                    ),
                ),
        );
    }

    private showPaylines(paylines: WinLine[]) {
        (this.controllers.get('payline') as PaylineController)?.showPaylines(
            paylines.map((win) => win.meta.lineIndex) as Paylines,
        );
    }

    private getSymbolIDByPosition(reel: number, row: number) {
        return this.reelController.getSymbolIDByPosition(reel, row);
    }

    setBet(betAmount?: number) {
        this.spine.texts.set('bet', `${betAmount ? betAmount / 1000000 : ''}`, true);
    }

    setBalance(balance: { amount: number; currency: string }) {
        // the displayed amount is rounded, so a change too small to show (or the
        // first balance there is) must not play a direction animation
        const previous = this.balance;
        this.balance = balance.amount;

        if (previous !== undefined && previous.toFixed(2) !== balance.amount.toFixed(2)) {
            this.spine.animations.playState(
                previous < balance.amount ? 'balance_up' : 'balance_down',
            );
        }

        this.spine.texts.set(
            'balance',
            `${balance.amount.toFixed(2)} ${balance.currency.toUpperCase()}`,
            true,
        );
    }

    async setTotalWin(win: number | string | undefined, level = 0) {
        if (Number(win) === 0) {
            await this.spine.texts.set('total_win', `-`);
            return;
        }

        const duration = level < 3 ? 0 : level < 6 ? 3000 : 5000;

        await this.spine.texts.set(
            'total_win',
            win !== undefined ? formatWin(win) : '-',
            true,
            duration,
        );
    }

    private async setWinAmountText(win: number | string | undefined) {
        if (win === 0) {
            await this.spine.texts.set('win', ``, true);
            return;
        }

        await this.spine.texts.set('win', win !== undefined ? formatWin(win) : '', true);
    }

    onSpinStart() {
        if (!this.freeSpinsActive) {
            this.spine.texts.set('freeSpinsProgress', ``); // reset free spins progress on each spin start
        }
    }
}
